import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProjectCard from "@/components/ProjectCard";
import QRCodeModal from "@/components/QRCodeModal";
import { Plus, QrCode, ArrowUpRight, PencilSimple, Trash, Folder, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import AdSlot from "@/components/AdSlot";

export default function Dashboard() {
    const { user } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qrOpen, setQrOpen] = useState(false);

    const profileUrl = `${window.location.origin}/b/${user?.slug}`;

    const load = async () => {
        try {
            const { data } = await api.get("/projects");
            setProjects(data);
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const remove = async (id) => {
        if (!window.confirm("Delete this project? This cannot be undone.")) return;
        try {
            await api.delete(`/projects/${id}`);
            setProjects(projects.filter((p) => p.id !== id));
            toast.success("Project deleted");
        } catch {
            toast.error("Failed to delete");
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
            {/* Header card */}
            <div className="border border-black bg-white">
                <div className="grid md:grid-cols-12">
                    <div className="md:col-span-8 p-8 sm:p-10 border-b md:border-b-0 md:border-r border-black">
                        <div className="text-overline text-[#E63946] mb-2">Builder dashboard</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none uppercase">
                            Hi, {user?.name?.split(" ")[0] || "Builder"}.
                        </h1>
                        <p className="mt-3 text-neutral-600 max-w-xl">
                            Manage your projects and share your QR with customers. Your public profile lives at{" "}
                            <Link to={`/b/${user?.slug}`} className="text-[#E63946] font-semibold underline" data-testid="dashboard-profile-link">
                                /b/{user?.slug}
                            </Link>
                            .
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/projects/new" data-testid="dashboard-new-project-btn" className="btn-primary">
                                <Plus size={16} weight="bold" /> New project
                            </Link>
                            <button
                                onClick={() => setQrOpen(true)}
                                data-testid="dashboard-qr-btn"
                                className="btn-accent"
                            >
                                <QrCode size={16} weight="bold" /> Show QR code
                            </button>
                            <Link to={`/b/${user?.slug}`} data-testid="dashboard-view-public" className="btn-ghost">
                                <Eye size={16} /> View public profile
                            </Link>
                            <Link to="/settings" data-testid="dashboard-settings-link" className="btn-ghost">
                                Edit profile
                            </Link>
                        </div>
                    </div>
                    <div className="md:col-span-4 p-8 sm:p-10 grid grid-cols-2 gap-4 md:gap-0">
                        <div className="md:p-2">
                            <div className="text-overline text-neutral-500 mb-2">Projects</div>
                            <div className="font-display text-5xl font-black" data-testid="dashboard-project-count">
                                {projects.length}
                            </div>
                        </div>
                        <div className="md:p-2 md:border-l md:border-black md:pl-6">
                            <div className="text-overline text-neutral-500 mb-2">Photos</div>
                            <div className="font-display text-5xl font-black">
                                {projects.reduce((s, p) => s + (p.images?.length || 0), 0)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Projects */}
            <div className="mt-12 mb-6 flex items-end justify-between">
                <div>
                    <div className="text-overline text-[#E63946] mb-2">Your projects</div>
                    <h2 className="font-display text-3xl font-black tracking-tighter uppercase">Recent work</h2>
                </div>
                <Link to="/projects/new" className="hidden sm:inline-flex btn-ghost text-xs">
                    <Plus size={14} weight="bold" /> Add
                </Link>
            </div>

            {loading ? (
                <div className="text-overline text-neutral-500 py-12">Loading…</div>
            ) : projects.length === 0 ? (
                <div className="border border-black p-12 text-center">
                    <Folder size={40} weight="duotone" className="mx-auto mb-4" />
                    <h3 className="font-display text-2xl font-bold mb-2">No projects yet</h3>
                    <p className="text-sm text-neutral-600 mb-6">
                        Add your first project to start building your gallery.
                    </p>
                    <Link to="/projects/new" className="btn-accent inline-flex" data-testid="empty-new-project-btn">
                        <Plus size={16} weight="bold" /> Create first project
                    </Link>
                </div>
            ) : (
                <div className="gallery-grid">
                    {projects.map((p, i) => (
                        <div key={p.id} className="relative group">
                            <ProjectCard project={p} hrefBase="/projects" index={i} />
                            <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Link
                                    to={`/projects/${p.id}/edit`}
                                    data-testid={`edit-project-${p.id}`}
                                    className="bg-white border border-black p-2 hover:bg-black hover:text-white"
                                >
                                    <PencilSimple size={14} weight="bold" />
                                </Link>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        remove(p.id);
                                    }}
                                    data-testid={`delete-project-${p.id}`}
                                    className="bg-white border border-black p-2 hover:bg-[#E63946] hover:text-white hover:border-[#E63946]"
                                >
                                    <Trash size={14} weight="bold" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <QRCodeModal
                open={qrOpen}
                onClose={() => setQrOpen(false)}
                profileUrl={profileUrl}
                builderName={user?.business_name || user?.name}
            />

            <div className="mt-16">
                <AdSlot slot="sidebar" variant="card" limit={2} />
            </div>
        </div>
    );
}
